import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import { fetchPartnerVoiceCallAggregateSummary, fetchVoiceCallAggregates } from './voice-calls-api';
import { fetchVoiceUsageStart } from './overview/voice-tenant-overview-api';
import { resolveVoiceDateRange } from './voice-utils';

const PAGE_SIZE = 25;

export function useVoiceTenantCallAggregates(selectedDays: number = 0) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCursors, setPageCursors] = useState<Record<number, string | undefined>>({});

  const [prevSelectedDays, setPrevSelectedDays] = useState(selectedDays);
  if (selectedDays !== prevSelectedDays) {
    setPrevSelectedDays(selectedDays);
    setCurrentPage(1);
    setPageCursors({});
  }

  const cursor = pageCursors[currentPage];

  // All Time passes firstVoiceAgentCreatedAt as startDate — with no dates the API defaults to 90 days.
  const usageStartQuery = useQuery({
    queryKey: ['voiceUsageStart'],
    queryFn: fetchVoiceUsageStart,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: selectedDays === 0,
  });
  const dateRange = resolveVoiceDateRange(selectedDays, usageStartQuery.data?.firstVoiceAgentCreatedAt);
  const dateRangeIsResolved = selectedDays > 0 || usageStartQuery.isSuccess;

  const dateRangeError = selectedDays > 0 ? null : usageStartQuery.error;

  const aggregatesQuery = useQuery({
    queryKey: ['voiceCallAggregates', selectedDays, currentPage, cursor, dateRange.startDate],
    queryFn: () =>
      fetchVoiceCallAggregates({
        cursor,
        limit: PAGE_SIZE,
        ...dateRange,
      }),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    enabled: dateRangeIsResolved,
  });

  const summaryQuery = useQuery({
    queryKey: ['voiceCallAggregateSummary', selectedDays, dateRange.startDate, dateRange.endDate],
    queryFn: () => fetchPartnerVoiceCallAggregateSummary(dateRange),
    staleTime: 60_000,
    enabled: dateRangeIsResolved,
  });

  const goToNextPage = useCallback(() => {
    if (aggregatesQuery.data?.hasNextPage && aggregatesQuery.data.cursor) {
      setPageCursors(prev => ({
        ...prev,
        [currentPage + 1]: aggregatesQuery.data!.cursor,
      }));
      setCurrentPage(p => p + 1);
    }
  }, [aggregatesQuery.data, currentPage]);

  const goToPreviousPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(p => p - 1);
    }
  }, [currentPage]);

  return {
    data: aggregatesQuery.data?.data ?? [],
    summary: summaryQuery.data,
    dateRange,
    dateRangeIsResolved,
    isLoading: !dateRangeError && aggregatesQuery.isPending,
    isFetching: aggregatesQuery.isFetching || summaryQuery.isFetching,
    summaryIsLoading: !dateRangeError && summaryQuery.isPending,
    summaryError: dateRangeError ?? summaryQuery.error,
    error: dateRangeError ?? aggregatesQuery.error,
    hasNextPage: aggregatesQuery.data?.hasNextPage ?? false,
    currentPage,
    goToNextPage,
    goToPreviousPage,
  };
}
